import { auth } from "firebase";
import AuthInfo from "@/service/auth_info";

class AuthService {
  /**
   * Creates a Firebase account, starts a session and creates the proper
   * configuration for the authenticated user.
   */
  static async signUp(email, password, restaurant, onEmailVerified) {
    try {
      let userCredentials = await auth().createUserWithEmailAndPassword(
        email,
        password
      );

      // Init restaurant
      restaurant.user = userCredentials.user.uid;
      await restaurant.save();

      // Send email
      await this.sendVerificationEmail();

      await this.onEmailVerified(onEmailVerified);
    } catch (error) {
      switch (error.code) {
        case "auth/email-already-in-use": // Thrown if there already exists an account with the given email address.
        case "auth/invalid-email": // Thrown if the email address is not valid.
        case "auth/operation-not-allowed": // Thrown if email/password accounts are not enabled. Enable email/password accounts in the Firebase Console, under the Auth tab.
        case "auth/weak-password": // Thrown if the password is not strong enough.
          throw new Error(error.code.substr(5)); // return just the code
        default:
          console.error(error);
          // TODO Do something here too
          throw new Error(error);
      }
    }
  }

  static async onEmailVerified(onEmailVerified) {
    let timeStarted = new Date();
    const whenMailIsVerified = async (fn, time) => {
      // More than 10 minutes
      if (new Date() - timeStarted > 10 * 60 * 1000) {
        return fn(false);
      }
      await auth().currentUser.reload();
      if (auth().currentUser.emailVerified) {
        return fn(true);
      }
      setTimeout(async () => await whenMailIsVerified(fn, time * 1.2), time);
    };
    // Wait for a while
    return await whenMailIsVerified(onEmailVerified, 1000);
  }

  /**
   * Must be called when logged in.
   */
  static async sendVerificationEmail() {
    /**
     * TODO Any of these useful?
     * <h4>Error Codes</h4> <dl>
     * auth/missing-android-pkg-name: // <dd>An Android package name must be provided if the Android app is required to be installed.</dd>
     * auth/missing-continue-uri: // <dd>A continue URL must be provided in the request.</dd>
     * auth/missing-ios-bundle-id: // <dd>An iOS bundle ID must be provided if an App Store ID is provided.</dd>
     * auth/invalid-continue-uri: // <dd>The continue URL provided in the request is invalid.</dd>
     * auth/unauthorized-continue-uri: // <dd>The domain of the continue URL is not whitelisted. Whitelist the domain in the Firebase console.</dd> </dl>
     */
    await auth().currentUser.sendEmailVerification();
  }

  static async verifyPassword(password) {
    try {
      let credential = auth.EmailAuthProvider.credential(
        auth().currentUser.email,
        password
      );

      await auth().currentUser.reauthenticateWithCredential(credential);
    } catch (error) {
      switch (error.code) {
        case "auth/user-mismatch": // : // <dd>Thrown if the credential given does not correspond to the user.</dd>
        case "auth/user-not-found": // : // <dd>Thrown if the credential given does not correspond to any existing user. </dd>
        case "auth/invalid-credential": // : // <dd>Thrown if the provider's credential is not valid. This can happen if it has already expired when calling link, or if it used invalid token(s). See the Firebase documentation for your provider, and make sure you pass in the correct parameters to the credential method.</dd>
        case "auth/invalid-email": // : // <dd>Thrown if the email used in a {@link firebase.auth.EmailAuthProvider.credential} is invalid.</dd>
        case "auth/wrong-password": // : // <dd>Thrown if the password used in a {@link firebase.auth.EmailAuthProvider.credential} is not correct or when the user associated with the email does not have a password.</dd>
        case "auth/invalid-verification-code": // : // <dd>Thrown if the credential is a {@link firebase.auth.PhoneAuthProvider.credential} and the verification code of the credential is not valid.</dd>
        case "auth/invalid-verification-id": // : // <dd>Thrown if the credential is a {@link firebase.auth.PhoneAuthProvider.credential} and the verification ID of the credential is not valid.</dd> </dl>
          throw new Error(error.code.substr(5)); // return just the code
        default:
          console.error(error);
          // TODO Do something here too
          throw new Error(error);
      }
      // TODO Handle error
    }
  }

  static async updatePassword(password) {
    try {
      await auth().currentUser.updatePassword(password);
    } catch (error) {
      switch (error.code) {
        case "auth/weak-password": //: // <dd>Thrown if the password is not strong enough.</dd>
        case "auth/requires-recent-login": //: // <dd>Thrown if the user's last sign-in time does not meet the security threshold. Use {@link firebase.User.reauthenticateWithCredential} to resolve. This does not apply if the user is anonymous.</dd>
          throw new Error(error.code.substr(5));
        default:
          console.error(error);
          // TODO Do something here too
          throw new Error(error);
      }
    }
  }

  static async deleteAccount() {
    await auth().currentUser.delete();
  }

  /**
   * Starts a Firebase session and creates the proper configuration for the
   * authenticated user.
   */
  static async logIn(email, password) {
    try {
      await auth().signInWithEmailAndPassword(email, password);
      return true;
    } catch (error) {
      switch (error.code) {
        // TODO Handle errors
        case "auth/invalid-email": // Thrown if the email address is not valid.
          break;
        case "auth/user-disabled": // Thrown if the user corresponding to the given email has been disabled.
          break;
        case "auth/user-not-found": // Thrown if there is no user corresponding to the given email.
          break;
        case "auth/wrong-password": // Thrown if the password is invalid for the given email, or the account corresponding to the email does not have a password set.</dd>
          break;
        default:
        // console.error(error);
        // TODO Do something here too
      }
      //alert("Something went wrong");

      return false;
    }
  }

  static async restorePassword(email) {
    try {
      await auth().sendPasswordResetEmail(email);
    } catch (error) {
      switch (error.code) {
        case "auth/invalid-email": // <dd>Thrown if the email address is not valid.</dd>
        case "auth/missing-android-pkg-name": // <dd>An Android package name must be provided if the Android app is required to be installed.</dd>
        case "auth/missing-continue-uri": // <dd>A continue URL must be provided in the request.</dd>
        case "auth/missing-ios-bundle-id": // <dd>An iOS Bundle ID must be provided if an App Store ID is provided.</dd>
        case "auth/invalid-continue-uri": // <dd>The continue URL provided in the request is invalid.</dd>
        case "auth/unauthorized-continue-uri": // <dd>The domain of the continue URL is not whitelisted. Whitelist the domain in the Firebase console.</dd>
        case "auth/user-not-found": // <dd>Thrown if there is no user corresponding to the email address.</dd> </dl>
          throw new Error(error.code.substr(5)); // return just the code
        default:
          console.error(error);
          // TODO Do something here too
          throw new Error(error);
      }
    }
  }

  /**
   * Logs out from Firebase and removes data for logged in user if any.
   */
  static logOut() {
    auth().signOut();
  }

  /**
   *  Provides a subscription to a change in the auth status in Firebase:
   *
   * ```
   * AuthService.onAuthStateChanged(function(user) {
   *   if (user) {
   *     // User is signed in.
   *   }
   * });
   * ```
   *
   * @param {function} fn Callback to interact with a returned `firebase.User`.
   *
   */
  static onAuthStateChanged(fn) {
    auth().onAuthStateChanged(user => {
      fn(user);
    });
  }

  static get info() {
    return this._authInfo;
  }

  static get isSignedIn() {
    return auth().currentUser !== null;
  }

  static get emailVerified() {
    return auth().currentUser.emailVerified;
  }
}

if (!AuthService._authInfo) {
  AuthService._authInfo = new AuthInfo();
}

export default AuthService;
